import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { client } from 'src/utils/api-client';
import {
  TabButton,
  TabContainer,
} from '../../../components/TabGroup/index.styles';
import Title from '../../../components/Title';
import { useGhgOrganization } from 'src/common/hooks';
import { getTokenAPI } from '../../../utils';
import {
  getFirstDateOfPeriod,
  timeConverter,
} from '../../../utils/timeConverter';
import Box from '../../components/Box';
import GraphComponent from '../../components/Dashboard/GraphComponent';
import Icon from '../../components/Icon';
import { Upload, Download } from '@mui/icons-material';
import {
  IntuitiveCalendarCollapsable,
  IntuitiveCalendarPeriod,
} from '../../components/IntuitiveCalendar';
import QuestionnaireTitle from '../../components/Questionnaire/Title';
import { Row } from '../Dashboard/index.styles';
import {
  PageContainer,
  SectionGroupContainer,
} from '../Questionnaire/index.styles';
import {
  ParticipationBodyCell,
  ParticipationHead,
  ParticipationHeadCell,
  ParticipationTable,
  StyledInput,
} from './index.styles';
import styles from './styles.module.css';
import DataEntryCompletedCheckbox from 'src/Ghg/components/DataEntryCompletedCheckbox';
import { GhgDataEntryCompletedType } from 'src/Ghg/components/DataEntryCompletedCheckbox/interfaces';
import EmployeeSurveyPreviewTable from 'src/Ghg/EmployeeSurveys/PreviewTable';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import { StyledPanel } from 'src/components/StyledPanel';
import { useUploadEmployeeEmissions } from 'src/Ghg/hooks';
import ConfirmationModal from 'src/components/ConfirmationModal';
import CopyPeriodForm from 'src/Ghg/EmployeeSurveys/CopyPeriodForm';

export default function EmployeesEmissions() {
  const organization = useGhgOrganization();
  const canWriteData = organization.can_write_data;
  const reportingPeriod = organization.co2.reporting_freq;
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current tab from URL, default to first available tab
  const defaultTab = canWriteData ? 'download-link' : 'check-results';
  const currentTab = searchParams.get('tab') || defaultTab;

  const handleTabClick = (tabId: string) => {
    setSearchParams({ tab: tabId });
  };

  // Set default tab in URL if none is specified
  useEffect(() => {
    if (!searchParams.get('tab')) {
      setSearchParams({ tab: defaultTab });
    }
  }, [searchParams, setSearchParams, defaultTab]);

  const [apiKey, setApiKey] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [uploadPeriodDate, setUploadPeriodDate] = useState<Date | null>(
    new Date()
  );
  const [activeStartDate, setActiveStartDate] = useState<Date | null>(
    new Date(new Date().toDateString())
  );
  const [activeEndDate, setActiveEndDate] = useState<Date | null>(null);
  const [figuresDate, setFiguresDate] = useState<Date | null>(new Date());
  const [textValue, setTextValue] = useState('Loading...');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [uploadErrorOpen, setUploadErrorOpen] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null
  );
  const [applyExtrapolation, setApplyExtrapolation] = useState(true);
  const { t } = useTranslation();
  const { uploadXlsx, downloadTemplate } = useUploadEmployeeEmissions();

  const handleFileUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls,.csv';
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file) {
        setSelectedFile(file);
        setShowConfirmModal(true);
      }
    };
    input.click();
  };

  const handleConfirmUpload = () => {
    if (selectedFile) {
      const uploadPeriod = timeConverter(
        uploadPeriodDate || new Date(),
        reportingPeriod
      );

      uploadXlsx.mutate(
        {
          organizationId: organization.id,
          file: selectedFile,
          period: uploadPeriod,
          withoutExtrapolation: !applyExtrapolation,
        },
        {
          onError: (error: any) => {
            const { response } = error as any;
            const status = response?.status;

            let message = t(
              'employees-emissions.uploadErrorGeneric',
              'Upload failed. Please try again.'
            );

            if (response && status === 400) {
              const { data } = response;
              if (typeof data === 'string') {
                message = data;
              } else if (data?.detail) {
                message = data.detail;
              } else {
                try {
                  message = JSON.stringify(data);
                } catch (e) {
                  // keep default message
                }
              }
            }

            setUploadErrorMessage(message);
            setUploadErrorOpen(true);
          },
        }
      );
      setShowConfirmModal(false);
      setSelectedFile(null);
    }
  };

  const handleCancelUpload = () => {
    setShowConfirmModal(false);
    setSelectedFile(null);
  };

  const figuresPeriod = timeConverter(figuresDate, reportingPeriod);

  const {
    data: figures,
    isLoading: isFiguresLoading,
    isError: isFiguresError,
  } = useQuery(
    [
      'ghg',
      'employee_survey_figures',
      { organizationId: organization.id, figuresPeriod },
    ],
    () =>
      client
        .get(
          `/web/v2/employee_survey_figures?period=${figuresPeriod}&organization_id=${organization.id}`
        )
        .then((res) => res.data)
  );

  const {
    surveys_percentage_filled: ESPercentageOfCompleted,
    business_travel_emissions: businessTravelEmissions,
    employee_emissions: employeesStats,
    employees_means_of_transport: employeesMeansOfTransport,
  } = figures || {};

  useEffect(() => {
    if (!!selectedDate && !!activeStartDate && reportingPeriod) {
      getTokenAPI(
        `${organization.id}`,
        timeConverter(selectedDate, reportingPeriod),
        format(activeStartDate, 'yyyy-MM-dd'),
        !!activeEndDate ? format(activeEndDate, 'yyyy-MM-dd') : undefined
      ).then((res) => {
        if (!res.token) throw new Error(res.detail[0].msg);
        setApiKey(res.token);
      });
    }
  }, [selectedDate, reportingPeriod, activeStartDate, activeEndDate]);

  useEffect(() => {
    if (apiKey)
      setTextValue(`${window.location.origin}/employee_form?api-key=${apiKey}`);
  }, [apiKey]);

  useEffect(() => {
    setTextValue(t('employees-emissions.loading'));
  }, []);

  return (
    <PageContainer>
      <SectionGroupContainer>
        <QuestionnaireTitle text={t('menu.employee-surveys')} />

        <div>
          <p className={styles.subtitle}>{t('employees-emissions.desc')}</p>
        </div>

        <ParticipationTable>
          <ParticipationHead>
            <ParticipationHeadCell>
              {t('employees-emissions.modal-info.text1')}
            </ParticipationHeadCell>
            <ParticipationHeadCell>
              {t('employees-emissions.modal-info.text2')} [%]
            </ParticipationHeadCell>
          </ParticipationHead>
          <div>
            <ParticipationBodyCell>
              <div>500+</div>
              <div>8%</div>
            </ParticipationBodyCell>
            <ParticipationBodyCell>
              <div>251-500</div>
              <div>10%</div>
            </ParticipationBodyCell>
            <ParticipationBodyCell>
              <div>51-250</div>
              <div>15%</div>
            </ParticipationBodyCell>
            <ParticipationBodyCell>
              <div>11-50</div>
              <div>20%</div>
            </ParticipationBodyCell>
            <ParticipationBodyCell>
              <div>1-10</div>
              <div>50%</div>
            </ParticipationBodyCell>
          </div>
        </ParticipationTable>

        <StyledPanel margin="24px 0">
          <Typography>{t('employees-emissions.xlsxImportText')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t(
              'employees-emissions.responseKeyHint',
              'Tip: use the “Response key” column to group multiple rows that belong to the same person (e.g., one employee commuting by car and taxi). Use the same key across sheets.'
            )}
          </Typography>
          <br />
          <Button startIcon={<Upload />} onClick={handleFileUpload}>
            {t('employees-emissions.xlsxImportButton')}
          </Button>
          <Button
            variant="text"
            onClick={downloadTemplate}
            startIcon={<Download />}
          >
            {t('employees-emissions.downloadExcelTemplate')}
          </Button>
        </StyledPanel>

        {/* Custom TabGroup using URL parameters */}
        <div>
          <TabContainer>
            {canWriteData && (
              <TabButton
                active={currentTab === 'download-link'}
                onClick={() => handleTabClick('download-link')}
              >
                {t('employees-emissions.download')}
              </TabButton>
            )}
            <TabButton
              active={currentTab === 'check-results'}
              onClick={() => handleTabClick('check-results')}
            >
              {t('employees-emissions.check')}
            </TabButton>
            <TabButton
              active={currentTab === 'surveys-preview'}
              onClick={() => handleTabClick('surveys-preview')}
            >
              {t('employees-emissions.preview')}
            </TabButton>
          </TabContainer>

          <div>
            {canWriteData && currentTab === 'download-link' && (
              <>
                <div style={{ marginBottom: '48px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Title
                      title={t('employees-emissions.select-period')}
                      description={null}
                    />
                    <CopyPeriodForm />
                  </div>
                  <IntuitiveCalendarPeriod
                    reportingPeriod={reportingPeriod}
                    selectedDate={getFirstDateOfPeriod(
                      selectedDate ? new Date(selectedDate) : new Date(),
                      reportingPeriod ?? 'M'
                    )}
                    setSelectedDate={setSelectedDate}
                  />
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <Title
                    title={t('employees-emissions.select-active-period')}
                    description={null}
                  />
                  <div className={styles.inputWrapper}>
                    <IntuitiveCalendarCollapsable
                      startDate={activeStartDate}
                      setStartDate={setActiveStartDate}
                      endDate={activeEndDate}
                      setEndDate={setActiveEndDate}
                      calendarWidth={100}
                      minDate={new Date(new Date().toDateString())}
                      maxDate={null}
                    />
                  </div>
                </div>

                <DataEntryCompletedCheckbox
                  selectedDate={selectedDate}
                  type={GhgDataEntryCompletedType.SCOPE_3_EMPLOYEE_SURVEY}
                  sx={{ mt: '-24px' }}
                />
                <div>
                  <div className={styles.labelWrapper}>
                    <p className={styles.label}>
                      <Icon name="link" alt="link icon" />
                      {t('employees-emissions.link-title')}
                    </p>
                  </div>
                  <div className={styles.inputWrapper}>
                    <StyledInput readOnly value={textValue} type="text" />
                    <button
                      onClick={() => navigator.clipboard.writeText(textValue)}
                      className={styles.button}
                      type="button"
                    >
                      <Icon name="copy" alt="copy icon" />{' '}
                      {t('employees-emissions.button')}
                    </button>
                  </div>
                </div>
              </>
            )}

            {currentTab === 'check-results' && (
              <>
                <div style={{ marginBottom: '48px' }}>
                  <Title
                    title={t('employees-emissions.select-dashboard-period')}
                    description={null}
                  />
                  <IntuitiveCalendarPeriod
                    reportingPeriod={reportingPeriod}
                    selectedDate={getFirstDateOfPeriod(
                      figuresDate ? new Date(figuresDate) : new Date(),
                      reportingPeriod ?? 'M'
                    )}
                    setSelectedDate={setFiguresDate}
                  />
                </div>

                <Row>
                  <Box text="employees-emissions.employees-percent">
                    <GraphComponent
                      plotData={ESPercentageOfCompleted?.value}
                      error={isFiguresError}
                      errorMsg={ESPercentageOfCompleted?.error_msg}
                      errorType={ESPercentageOfCompleted?.error_type}
                      loading={isFiguresLoading}
                    />
                  </Box>

                  <Box text="dashboard.means">
                    <GraphComponent
                      plotData={employeesMeansOfTransport?.value}
                      error={isFiguresError}
                      errorMsg={employeesMeansOfTransport?.error_msg}
                      errorType={employeesMeansOfTransport?.error_type}
                      loading={isFiguresLoading}
                    />
                  </Box>
                </Row>

                <Row firstEl={40} secondEl={60}>
                  <Box text="employees-emissions.commuting-and-business">
                    <GraphComponent
                      plotData={employeesStats?.value}
                      error={isFiguresError}
                      errorMsg={employeesStats?.error_msg}
                      errorType={employeesStats?.error_type}
                      loading={isFiguresLoading}
                    />
                  </Box>

                  <Box text="dashboard.employee-emissions">
                    <GraphComponent
                      plotData={businessTravelEmissions?.value}
                      error={isFiguresError}
                      errorMsg={businessTravelEmissions?.error_msg}
                      errorType={businessTravelEmissions?.error_type}
                      loading={isFiguresLoading}
                    />
                  </Box>
                </Row>
              </>
            )}

            {currentTab === 'surveys-preview' && <EmployeeSurveyPreviewTable />}
          </div>
        </div>
      </SectionGroupContainer>

      <ConfirmationModal
        open={showConfirmModal}
        titleKey="employees-emissions.confirmUpload"
        textKey="employees-emissions.confirmUploadText"
        trueButtonTextKey="upload"
        falseButtonTextKey="cancel"
        onTrue={handleConfirmUpload}
        onFalse={handleCancelUpload}
        onClose={handleCancelUpload}
      >
        {selectedFile && (
          <>
            <Typography sx={{ textAlign: 'center', mt: 2 }}>
              <strong>{selectedFile.name}</strong>
            </Typography>
            <div style={{ marginTop: '24px' }}>
              <Title
                title={t('employees-emissions.select-period')}
                description={null}
              />
              <IntuitiveCalendarPeriod
                reportingPeriod={reportingPeriod}
                selectedDate={getFirstDateOfPeriod(
                  uploadPeriodDate ? new Date(uploadPeriodDate) : new Date(),
                  reportingPeriod ?? 'M'
                )}
                setSelectedDate={setUploadPeriodDate}
              />
            </div>
            <FormControlLabel
              control={
                <Switch
                  checked={applyExtrapolation}
                  onChange={(event) =>
                    setApplyExtrapolation(event.target.checked)
                  }
                />
              }
              label={t(
                'employees-emissions.extrapolationToggle',
                'Apply extrapolation to the imported survey'
              )}
              sx={{ mt: 2 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t(
                'employees-emissions.extrapolationHint',
                'Turn this off to save uploaded results exactly as provided (no employee-based scaling).'
              )}
            </Typography>
          </>
        )}
      </ConfirmationModal>

      <Dialog
        open={uploadErrorOpen}
        onClose={() => setUploadErrorOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography sx={{ textAlign: 'center' }} variant="h3">
            {t(
              'employees-emissions.uploadErrorTitle',
              'Employee survey upload error'
            )}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {uploadErrorMessage && (
            <Typography
              sx={{ textAlign: 'center', whiteSpace: 'pre-line', mt: 2 }}
            >
              {uploadErrorMessage}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
